import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  Typography,
  Spin,
  Alert,
  Tabs,
  Modal,
  Tag,
  Divider,
  Form,
  Select,
  Input,
  Space,
  Row,
  Col,
  Statistic,
  Table,
  Descriptions,
  Empty,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  MailOutlined,
  PhoneOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  ToolOutlined,
  SolutionOutlined,
  AppstoreOutlined,
  SafetyCertificateOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  PlusOutlined,
  ShopOutlined,
  UserOutlined,
  KeyOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { usersService } from '../../services/usersService';
import { useAuth } from '../../contexts/AuthContext';
import type { UserProfile as BaseUserProfile, UserStatus } from './types';
import { UserAvatar } from './components';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;

interface UserProfile extends BaseUserProfile {
  phone?: string | null;
  phoneNumber?: string | null;
  firstName?: string;
  lastName?: string;
  createdAt?: string;
  updatedAt?: string;
  specialty?: string;
  customerProfile?: any;
}

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  const isManager = currentUser?.role === 'manager';
  const isAgent = currentUser?.role === 'agent';
  const canEdit = isAdmin || isManager || isAgent;

  // États du composant
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // Données associées au client
  const [interventions, setInterventions] = useState<any[]>([]);
  const [loadingInterventions, setLoadingInterventions] = useState<boolean>(false);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [equipments, setEquipments] = useState<any[]>([]);

  // Dialogues
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState<boolean>(false);
  const [newStatus, setNewStatus] = useState<UserStatus>('active');
  const [updatingStatus, setUpdatingStatus] = useState<boolean>(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [changingPassword, setChangingPassword] = useState<boolean>(false);

  const statusLabel: Record<string, string> = {
    pending: 'En attente',
    assigned: 'Assignée',
    accepted: 'Acceptée',
    on_the_way: 'En route',
    arrived: 'Sur place',
    in_progress: 'En cours',
    diagnostic_submitted: 'Diagnostic soumis',
    execution_confirmed: 'Travaux confirmés',
    completed: 'Terminée',
    cancelled: 'Annulée',
    scheduled: 'Programmée',
    active: 'Actif',
    inactive: 'Inactif',
    suspended: 'Suspendu'
  };

  const statusColor: Record<string, string> = {
    pending: 'orange',
    assigned: 'purple',
    accepted: 'cyan',
    on_the_way: 'blue',
    arrived: 'geekblue',
    in_progress: 'processing',
    diagnostic_submitted: 'cyan',
    execution_confirmed: 'green',
    completed: 'success',
    cancelled: 'error',
    scheduled: 'blue',
    active: 'success',
    inactive: 'default',
    suspended: 'error'
  };

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      active: 'Actif',
      inactive: 'Inactif',
      suspended: 'Suspendu',
      pending: 'En attente'
    };
    return statusMap[status] || status;
  };

  // Charger les données de l'utilisateur et ses données liées
  useEffect(() => {
    const loadUserData = async () => {
      if (!id) return;
      const numId = parseInt(id, 10);
      if (isNaN(numId)) return;

      try {
        setLoading(true);
        setError(null);

        // 1. Récupérer l'utilisateur
        const userData = await usersService.getUserById(numId);
        if (!userData) {
          throw new Error('Utilisateur non trouvé');
        }

        const userProfile: UserProfile = {
          id: userData.id,
          email: userData.email,
          first_name: userData.first_name || userData.firstName || '',
          last_name: userData.last_name || userData.lastName || '',
          firstName: userData.firstName || userData.first_name || '',
          lastName: userData.lastName || userData.last_name || '',
          phone: userData.phone || userData.phoneNumber || null,
          phoneNumber: userData.phoneNumber || userData.phone || null,
          role: userData.role || 'customer',
          status: (userData.status as UserStatus) || 'active',
          profile_image: userData.profile_image || null,
          last_login: userData.last_login || null,
          created_at: userData.created_at || userData.createdAt || new Date().toISOString(),
          updated_at: userData.updated_at || userData.updatedAt || new Date().toISOString(),
          customerProfile: (userData as any).customerProfile || null,
          specialty: userData.specialty
        };

        setUser(userProfile);
        setNewStatus(userProfile.status);

        const customerProfileId = userProfile.customerProfile?.id;

        const extractArray = (resData: any, ...keys: string[]): any[] => {
          if (!resData) return [];
          if (Array.isArray(resData)) return resData;
          for (const k of keys) {
            if (Array.isArray(resData[k])) return resData[k];
          }
          if (resData.data) {
            if (Array.isArray(resData.data)) return resData.data;
            for (const k of keys) {
              if (Array.isArray(resData.data[k])) return resData.data[k];
            }
          }
          return [];
        };

        // 2. Charger les interventions
        setLoadingInterventions(true);
        try {
          const isTechnicianUser = userProfile.role === 'technician';
          const interventionParams: any = {
            limit: 1000,
            include_unpaid: 'true'
          };
          if (isTechnicianUser) {
            interventionParams.technician_id = numId;
          } else {
            interventionParams.customer_id = customerProfileId || numId;
          }

          const res = await api.get('/interventions', { params: interventionParams });
          const allList = extractArray(res.data, 'interventions');
          const filtered = isTechnicianUser
            ? allList.filter((item: any) =>
                Number(item.technician_id) === numId ||
                Number(item.technicianId) === numId ||
                Number(item.technician?.id) === numId ||
                Number(item.technician?.user_id) === numId
              )
            : allList.filter((item: any) => 
                Number(item.customer_id) === numId || 
                (customerProfileId && Number(item.customer_id) === Number(customerProfileId)) ||
                Number(item.customerId) === numId ||
                (customerProfileId && Number(item.customerId) === Number(customerProfileId)) ||
                Number(item.customer?.id) === numId ||
                (customerProfileId && Number(item.customer?.id) === Number(customerProfileId)) ||
                Number(item.customer?.user_id) === numId ||
                Number(item.customer?.user?.id) === numId
              );
          setInterventions(filtered);
        } catch (e) {
          console.warn('Impossible de charger les interventions:', e);
        } finally {
          setLoadingInterventions(false);
        }

        // 3. Charger les souscriptions / contrats (uniquement pour les clients)
        if (userProfile.role === 'customer') {
          try {
            const resSub = await api.get('/admin/subscriptions', {
              params: {
                includePending: 'true',
                customer_id: customerProfileId || numId
              }
            });
            const subs = extractArray(resSub.data, 'subscriptions');
            const filteredSubs = subs.filter((s: any) => 
              Number(s.customer_id) === numId || 
              (customerProfileId && Number(s.customer_id) === Number(customerProfileId)) ||
              Number(s.customer?.id) === numId ||
              (customerProfileId && Number(s.customer?.id) === Number(customerProfileId)) ||
              Number(s.customer?.user_id) === numId ||
              Number(s.customer?.user?.id) === numId
            );
            setSubscriptions(filteredSubs);
          } catch (e) {
            console.warn('Impossible de charger les abonnements du client:', e);
          }
        }

        // 4. Charger les équipements (splits) (uniquement pour les clients)
        if (userProfile.role === 'customer') {
          try {
            const resEq = await api.get('/splits', {
              params: {
                limit: 1000,
                customerId: numId
              }
            });
            const eqList = extractArray(resEq.data, 'splits');
            const filteredEq = eqList.filter((e: any) => 
              Number(e.customer_id) === numId || 
              (customerProfileId && Number(e.customer_id) === Number(customerProfileId)) ||
              Number(e.userId) === numId ||
              Number(e.customer?.id) === numId ||
              Number(e.customer?.user_id) === numId ||
              Number(e.customer?.user?.id) === numId
            );
            setEquipments(filteredEq);
          } catch (e) {
            console.warn('Impossible de charger les équipements:', e);
          }
        }

      } catch (err: any) {
        console.error('Erreur lors du chargement des données utilisateur:', err);
        setError('Impossible de charger les données de l\'utilisateur');
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [id]);

  // Handlers
  const handleUpdateStatus = async () => {
    if (!user || !id) return;
    try {
      setUpdatingStatus(true);
      const validStatus: UserStatus = 
        newStatus === 'suspended' ? 'suspended' :
        newStatus === 'inactive' ? 'inactive' :
        newStatus === 'pending' ? 'pending' : 'active';

      const updatedUser = await usersService.updateUser(parseInt(id, 10), {
        status: validStatus
      });

      if (!updatedUser) throw new Error('Échec de la mise à jour');

      setUser(prev => prev ? { ...prev, status: validStatus } : null);
      setStatusDialogOpen(false);
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleChangePassword = async () => {
    if (!id) return;
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    try {
      setChangingPassword(true);
      setError(null);
      await usersService.updateUser(parseInt(id, 10), { password: newPassword });
      setPasswordDialogOpen(false);
      setNewPassword('');
      setConfirmPassword('');
      Modal.success({ title: 'Succès', content: 'Mot de passe mis à jour avec succès.' });
    } catch (err) {
      console.error(err);
      setError('Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!id) return;
    try {
      setDeleting(true);
      await usersService.deleteUser(parseInt(id, 10));
      navigate('/users');
    } catch (err: any) {
      console.error(err);
      const msg = err.response?.data?.error || err.response?.data?.message || 'Erreur lors de la suppression';
      setError(msg);
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 60, textAlign: 'center' }}>
        <Spin size="large" tip="Chargement du profil client..." />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          message="Erreur de chargement"
          description={error || 'Utilisateur non trouvé'}
          type="error"
          showIcon
          action={
            <Button size="small" type="primary" onClick={() => navigate('/users')}>
              Retour à la liste
            </Button>
          }
        />
      </div>
    );
  }

  const cp = user.customerProfile || {};
  const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Client';

  // Colonnes pour le tableau des interventions
  const interventionColumns = [
    {
      title: 'Titre / Référence',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: any) => (
        <div>
          <Text strong style={{ color: '#0f172a' }}>{text || `Intervention #${record.id}`}</Text>
          {record.intervention_type && (
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Type: {record.intervention_type.toUpperCase()}
            </div>
          )}
        </div>
      )
    },
    user?.role === 'technician' ? {
      title: 'Client',
      dataIndex: 'customer',
      key: 'customer',
      render: (cust: any, record: any) => {
        const cName = cust?.first_name || cust?.last_name 
          ? `${cust.first_name || ''} ${cust.last_name || ''}`.trim() 
          : record.customer_name || 'Client';
        return <Text strong>{cName}</Text>;
      }
    } : {
      title: 'Technicien',
      dataIndex: 'technician',
      key: 'technician',
      render: (tech: any) => tech ? (
        <Space>
          <UserAvatar src={tech.profile_image} firstName={tech.first_name} lastName={tech.last_name} size="small" />
          <Text>{`${tech.first_name || ''} ${tech.last_name || ''}`}</Text>
        </Space>
      ) : <Text type="secondary">Non assigné</Text>
    },
    {
      title: 'Date prévue',
      dataIndex: 'scheduled_date',
      key: 'scheduled_date',
      render: (date: string) => date ? new Date(date).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '-'
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (st: string) => (
        <Tag color={statusColor[st] || 'default'} style={{ borderRadius: 12, padding: '2px 10px', fontWeight: 600 }}>
          {statusLabel[st] || st}
        </Tag>
      )
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: any) => (
        <Button
          size="small"
          type="primary"
          ghost
          icon={<EyeOutlined />}
          onClick={() => navigate(`/interventions?id=${record.id}`)}
        >
          Consulter
        </Button>
      )
    }
  ];

  // Colonnes pour les souscriptions
  const subscriptionColumns = [
    {
      title: 'Offre / Formule',
      dataIndex: 'offer_snapshot',
      key: 'offer',
      render: (snapshot: any, record: any) => (
        <div>
          <Text strong>{snapshot?.title || record.offer?.title || `Contrat #${record.id}`}</Text>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            {record.equipment_count || 1} équipement(s)
          </div>
        </div>
      )
    },
    {
      title: 'Visites',
      key: 'visits',
      render: (_: any, record: any) => (
        <Tag color="blue" style={{ fontWeight: 600 }}>
          {record.visits_completed || 0} / {record.visits_total || 4} visites
        </Tag>
      )
    },
    {
      title: 'Paiement',
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (st: string) => (
        <Tag color={st === 'paid' ? 'green' : st === 'partial' ? 'orange' : 'volcano'}>
          {st === 'paid' ? 'Payé (100%)' : st === 'partial' ? 'Acompte 50%' : 'En attente'}
        </Tag>
      )
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (st: string) => (
        <Tag color={st === 'active' ? 'success' : st === 'used' ? 'purple' : 'default'}>
          {st === 'active' ? 'Actif' : st === 'used' ? 'Terminé' : st}
        </Tag>
      )
    }
  ];

  return (
    <div style={{ padding: '16px 24px 40px 24px' }}>
      
      {/* Barre de navigation supérieure */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <Space size="middle">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              if (user.role === 'technician') {
                navigate('/techniciens');
              } else if (user.role === 'customer') {
                navigate('/clients');
              } else {
                navigate('/utilisateurs');
              }
            }}
            style={{ borderRadius: 8 }}
          >
            Retour
          </Button>
          <Title level={4} style={{ margin: 0, fontWeight: 700, color: '#0f172a' }}>
            {user.role === 'technician' ? `Fiche Technicien #${user.id}` : user.role === 'customer' ? `Fiche Client #${user.id}` : `Fiche Utilisateur #${user.id}`}
          </Title>
        </Space>
        
        <Space size="small">
          {user.role === 'customer' && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{ backgroundColor: '#0a543d', borderColor: '#0a543d', fontWeight: 600, borderRadius: 8 }}
              onClick={() => navigate('/interventions')}
            >
              Nouvelle Intervention
            </Button>
          )}
          {canEdit && (
            <Button
              icon={<EditOutlined />}
              style={{ borderRadius: 8 }}
              onClick={() => navigate(`/users/edit/${user.id}`)}
            >
              Modifier
            </Button>
          )}
          {isAdmin && (
            <Button
              danger
              icon={<DeleteOutlined />}
              style={{ borderRadius: 8 }}
              onClick={handleDeleteConfirm}
              disabled={user.role === 'admin' || user.role === 'super_admin' || String(user.id) === String(currentUser?.id)}
            >
              Supprimer
            </Button>
          )}
        </Space>
      </div>

      {/* HERO BANNER - CARTE DE PROFIL CLIENT HAUT DE GAMME */}
      <Card
        bordered={false}
        style={{
          background: 'linear-gradient(135deg, #0a543d 0%, #11694e 60%, #063828 100%)',
          borderRadius: 16,
          boxShadow: '0 10px 25px rgba(10, 84, 61, 0.22)',
          marginBottom: 24,
          position: 'relative',
          overflow: 'hidden'
        }}
        bodyStyle={{ padding: '28px 32px' }}
      >
        <ToolOutlined
          style={{
            position: 'absolute',
            right: -20,
            bottom: -30,
            fontSize: 220,
            color: 'rgba(255, 255, 255, 0.04)',
            pointerEvents: 'none'
          }}
        />

        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} md={16}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
              <div style={{ position: 'relative' }}>
                <UserAvatar
                  src={user.profile_image || undefined}
                  firstName={user.first_name}
                  lastName={user.last_name}
                  size={84}
                />
                <span
                  style={{
                    position: 'absolute',
                    bottom: 2,
                    right: 2,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    backgroundColor: user.status === 'active' ? '#22c55e' : '#f59e0b',
                    border: '3px solid #ffffff'
                  }}
                />
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
                  <h1 style={{ color: '#ffffff', margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em' }}>
                    {fullName}
                  </h1>
                  <Tag color="gold" style={{ fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 12, border: 'none' }}>
                    {user.role === 'super_admin' ? 'Super Administrateur' : user.role === 'admin' ? 'Administrateur' : user.role === 'manager' ? 'Team Lead' : user.role === 'agent' ? 'Agent' : user.role === 'technician' ? 'Technicien' : 'Client VIP'}
                  </Tag>
                  <Tag
                    color={user.status === 'active' ? 'success' : 'warning'}
                    style={{ fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 12 }}
                  >
                    {getStatusLabel(user.status)}
                  </Tag>
                </div>

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
                  <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '4px 14px', borderRadius: 20, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, backdropFilter: 'blur(4px)' }}>
                    <MailOutlined style={{ color: '#EEBD1B' }} /> {user.email}
                  </span>
                  {user.phone && (
                    <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '4px 14px', borderRadius: 20, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, backdropFilter: 'blur(4px)' }}>
                      <PhoneOutlined style={{ color: '#4ade80' }} /> {user.phone}
                    </span>
                  )}
                  {(cp.city || cp.commune) && (
                    <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', padding: '4px 14px', borderRadius: 20, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6, backdropFilter: 'blur(4px)' }}>
                      <EnvironmentOutlined style={{ color: '#60a5fa' }} /> {[cp.commune, cp.city].filter(Boolean).join(', ')}
                    </span>
                  )}
                  <span style={{ background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.85)', padding: '4px 14px', borderRadius: 20, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <CalendarOutlined /> Inscrit le {new Date(user.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </div>
            </div>
          </Col>

          {canEdit && (
            <Col xs={24} md={8} style={{ textAlign: 'right' }}>
              <Space direction="vertical" size="small" style={{ width: '100%', alignItems: 'flex-end' }}>
                <Button
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                    color: '#ffffff',
                    borderRadius: 8,
                    fontWeight: 600
                  }}
                  icon={<SafetyCertificateOutlined />}
                  onClick={() => setStatusDialogOpen(true)}
                >
                  Changer le Statut Compte
                </Button>
                <Button
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderColor: 'rgba(255, 255, 255, 0.4)',
                    color: '#ffffff',
                    borderRadius: 8,
                    fontWeight: 600
                  }}
                  icon={<KeyOutlined />}
                  onClick={() => setPasswordDialogOpen(true)}
                >
                  Changer Mot de passe
                </Button>
              </Space>
            </Col>
          )}
        </Row>
      </Card>

      {/* BARRE DE CARTES KPI SYNTHÈSE */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={user.role === 'customer' ? 6 : 12}>
          <Card
            bordered={false}
            style={{
              background: '#FFF7ED',
              border: '1px solid #FFEDD5',
              borderRadius: 12,
              boxShadow: '0 2px 8px rgba(234, 88, 12, 0.05)'
            }}
            bodyStyle={{ padding: 18 }}
          >
            <Statistic
              title={<Text style={{ color: '#C2410C', fontWeight: 600, fontSize: 13 }}>{user.role === 'technician' ? 'Interventions Assignées' : 'Interventions Total'}</Text>}
              value={interventions.length}
              prefix={<ToolOutlined style={{ color: '#EA580C', marginRight: 8 }} />}
              valueStyle={{ color: '#9A3412', fontWeight: 700, fontSize: 24 }}
            />
            <div style={{ marginTop: 4, fontSize: 12, color: '#EA580C' }}>
              {user.role === 'technician' ? 'Missions attribuées' : 'Demandes enregistrées'}
            </div>
          </Card>
        </Col>

        {user.role === 'customer' && (
          <>
            <Col xs={12} sm={6}>
              <Card
                bordered={false}
                style={{
                  background: '#F0FDF4',
                  border: '1px solid #DCFCE7',
                  borderRadius: 12,
                  boxShadow: '0 2px 8px rgba(5, 150, 105, 0.05)'
                }}
                bodyStyle={{ padding: 18 }}
              >
                <Statistic
                  title={<Text style={{ color: '#15803D', fontWeight: 600, fontSize: 13 }}>Contrats & Abonnements</Text>}
                  value={subscriptions.length}
                  prefix={<SolutionOutlined style={{ color: '#059669', marginRight: 8 }} />}
                  valueStyle={{ color: '#166534', fontWeight: 700, fontSize: 24 }}
                />
                <div style={{ marginTop: 4, fontSize: 12, color: '#16A34A' }}>
                  Formules de maintenance
                </div>
              </Card>
            </Col>

            <Col xs={12} sm={6}>
              <Card
                bordered={false}
                style={{
                  background: '#EFF6FF',
                  border: '1px solid #DBEAFE',
                  borderRadius: 12,
                  boxShadow: '0 2px 8px rgba(37, 99, 235, 0.05)'
                }}
                bodyStyle={{ padding: 18 }}
              >
                <Statistic
                  title={<Text style={{ color: '#1D4ED8', fontWeight: 600, fontSize: 13 }}>Climatiseurs & Splits</Text>}
                  value={equipments.length}
                  prefix={<AppstoreOutlined style={{ color: '#2563EB', marginRight: 8 }} />}
                  valueStyle={{ color: '#1E40AF', fontWeight: 700, fontSize: 24 }}
                />
                <div style={{ marginTop: 4, fontSize: 12, color: '#2563EB' }}>
                  Parc d'équipements
                </div>
              </Card>
            </Col>
          </>
        )}

        <Col xs={12} sm={user.role === 'customer' ? 6 : 12}>
          <Card
            bordered={false}
            style={{
              background: '#FAF5FF',
              border: '1px solid #F3E8FF',
              borderRadius: 12,
              boxShadow: '0 2px 8px rgba(124, 58, 237, 0.05)'
            }}
            bodyStyle={{ padding: 18 }}
          >
            <Statistic
              title={<Text style={{ color: '#6D28D9', fontWeight: 600, fontSize: 13 }}>Statut du Compte</Text>}
              value={getStatusLabel(user.status)}
              prefix={<SafetyCertificateOutlined style={{ color: '#7C3AED', marginRight: 8 }} />}
              valueStyle={{ color: '#5B21B6', fontWeight: 700, fontSize: 20 }}
            />
            <div style={{ marginTop: 4, fontSize: 12, color: '#7C3AED' }}>
              Compte vérifié
            </div>
          </Card>
        </Col>
      </Row>

      {/* CONTENU ONGLETS PRINCIPAUX */}
      <Card
        bordered={false}
        style={{ borderRadius: 16, boxShadow: '0 4px 18px rgba(0,0,0,0.04)' }}
        bodyStyle={{ padding: '20px 24px' }}
      >
        <Tabs
          defaultActiveKey="0"
          size="large"
          items={[
            {
              key: '0',
              label: (
                <span style={{ fontWeight: 600, fontSize: 15 }}>
                  <UserOutlined /> Identité & Coordonnées
                </span>
              ),
              children: (
                <div style={{ paddingTop: 16 }}>
                  <Row gutter={[24, 24]}>
                    <Col xs={24} md={12}>
                      <Card title={<Text strong style={{ fontSize: 15 }}><UserOutlined /> Identité Personnelle</Text>} size="small" style={{ borderRadius: 12 }}>
                        <Descriptions column={1} size="middle" bordered>
                          <Descriptions.Item label="Nom complet">
                            <Text strong style={{ color: '#0f172a' }}>{fullName}</Text>
                          </Descriptions.Item>
                          <Descriptions.Item label="Email principal">
                            <Text copyable={{ text: user.email }}>{user.email}</Text>
                          </Descriptions.Item>
                          <Descriptions.Item label="Téléphone">
                            <Text copyable={{ text: user.phone || '' }}>{user.phone || 'Non renseigné'}</Text>
                          </Descriptions.Item>
                          <Descriptions.Item label="Rôle Système">
                            <Tag color="blue">{user.role.toUpperCase()}</Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="Statut Compte">
                            <Tag color={statusColor[user.status]}>{getStatusLabel(user.status)}</Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="Dernière connexion">
                            {user.last_login ? new Date(user.last_login).toLocaleString('fr-FR') : 'Non renseigné'}
                          </Descriptions.Item>
                        </Descriptions>
                      </Card>
                    </Col>

                    <Col xs={24} md={12}>
                      <Card title={<Text strong style={{ fontSize: 15 }}><EnvironmentOutlined /> {user.role === 'technician' ? 'Informations Professionnelles' : 'Localisation & Structure'}</Text>} size="small" style={{ borderRadius: 12 }}>
                        <Descriptions column={1} size="middle" bordered>
                          {user.role === 'technician' ? (
                            <>
                              <Descriptions.Item label="Profil">
                                Technicien de maintenance
                              </Descriptions.Item>
                              <Descriptions.Item label="Spécialité / Métier">
                                {user.specialty || 'Climatisation & Froid'}
                              </Descriptions.Item>
                              <Descriptions.Item label="Disponibilité">
                                <Tag color={user.status === 'active' ? 'green' : 'default'}>{user.status === 'active' ? 'Opérationnel' : getStatusLabel(user.status)}</Tag>
                              </Descriptions.Item>
                              <Descriptions.Item label="Date d'intégration">
                                {new Date(user.created_at).toLocaleDateString('fr-FR')}
                              </Descriptions.Item>
                            </>
                          ) : (
                            <>
                              <Descriptions.Item label="Entreprise / Structure">
                                {cp.company_name || 'Particulier'}
                              </Descriptions.Item>
                              <Descriptions.Item label="Type d'activité">
                                {cp.company_type || 'Particulier'}
                              </Descriptions.Item>
                              <Descriptions.Item label="Pays">
                                {cp.country || 'Côte d\'Ivoire'}
                              </Descriptions.Item>
                              <Descriptions.Item label="Ville">
                                {cp.city || 'Abidjan'}
                              </Descriptions.Item>
                              <Descriptions.Item label="Commune / Quartier">
                                {cp.commune || 'Non renseigné'}
                              </Descriptions.Item>
                              <Descriptions.Item label="Coordonnées GPS">
                                {cp.latitude && cp.longitude ? `${cp.latitude}, ${cp.longitude}` : 'Non localisé'}
                              </Descriptions.Item>
                            </>
                          )}
                        </Descriptions>
                      </Card>
                    </Col>
                  </Row>
                </div>
              )
            },
            {
              key: '1',
              label: (
                <span style={{ fontWeight: 600, fontSize: 15 }}>
                  <ToolOutlined /> {user.role === 'technician' ? 'Interventions Assignées' : 'Interventions'} ({interventions.length})
                </span>
              ),
              children: (
                <div style={{ paddingTop: 16 }}>
                  {loadingInterventions ? (
                    <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
                  ) : interventions.length === 0 ? (
                    <Empty
                      description={user.role === 'technician' ? 'Aucune intervention assignée à ce technicien' : 'Aucune intervention trouvée pour ce client'}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    >
                      {user.role === 'customer' && canEdit && (
                        <Button type="primary" style={{ backgroundColor: '#0a543d', borderColor: '#0a543d' }} onClick={() => navigate('/interventions')}>
                          Créer une intervention
                        </Button>
                      )}
                    </Empty>
                  ) : (
                    <Table
                      dataSource={interventions}
                      columns={interventionColumns}
                      rowKey="id"
                      pagination={{ pageSize: 5 }}
                    />
                  )}
                </div>
              )
            },
            ...(user.role === 'customer' ? [
              {
                key: '2',
                label: (
                  <span style={{ fontWeight: 600, fontSize: 15 }}>
                    <SolutionOutlined /> Abonnements & Contrats ({subscriptions.length})
                  </span>
                ),
                children: (
                  <div style={{ paddingTop: 16 }}>
                    {subscriptions.length === 0 ? (
                      <Empty description="Aucun abonnement de maintenance actif pour ce client" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                      <Table
                        dataSource={subscriptions}
                        columns={subscriptionColumns}
                        rowKey="id"
                        pagination={{ pageSize: 5 }}
                      />
                    )}
                  </div>
                )
              },
              {
                key: '3',
                label: (
                  <span style={{ fontWeight: 600, fontSize: 15 }}>
                    <AppstoreOutlined /> Équipements Climatisation ({equipments.length})
                  </span>
                ),
                children: (
                  <div style={{ paddingTop: 16 }}>
                    {equipments.length === 0 ? (
                      <Empty description="Aucun équipement de climatisation enregistré pour ce client" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ) : (
                      <Row gutter={[16, 16]}>
                        {equipments.map((eq: any) => (
                          <Col xs={24} sm={12} md={8} key={eq.id}>
                            <Card size="small" style={{ borderRadius: 12, border: '1px solid #e2e8f0' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                <ShopOutlined style={{ fontSize: 24, color: '#0a543d' }} />
                                <div>
                                  <Text strong>{eq.brand || 'Climatiseur'} {eq.model || ''}</Text>
                                  <div style={{ fontSize: 12, color: '#64748b' }}>{eq.type || 'Mural'}</div>
                                </div>
                              </div>
                              <Divider style={{ margin: '8px 0' }} />
                              <div style={{ fontSize: 13, color: '#334155' }}>
                                <div><strong>Puissance:</strong> {eq.power || eq.puissance || 'N/A'} CV</div>
                                <div><strong>Gaz Fréon:</strong> {eq.freon || eq.type_freon || 'R410'}</div>
                                <div><strong>Emplacement:</strong> {eq.location || eq.emplacement || 'Salon'}</div>
                              </div>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    )}
                  </div>
                )
              }
            ] : []),
            ...(canEdit ? [
              {
                key: '4',
                label: (
                  <span style={{ fontWeight: 600, fontSize: 15 }}>
                    <LockOutlined /> Sécurité & Paramètres
                  </span>
                ),
                children: (
                  <div style={{ paddingTop: 16 }}>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        <Card title="Mot de Passe" size="small" style={{ borderRadius: 12 }}>
                          <Paragraph type="secondary">
                            Modifiez le mot de passe d'accès pour ce compte.
                          </Paragraph>
                          <Button type="primary" icon={<KeyOutlined />} onClick={() => setPasswordDialogOpen(true)}>
                            Changer le mot de passe
                          </Button>
                        </Card>
                      </Col>
                      <Col xs={24} md={12}>
                        <Card title="Statut du Compte" size="small" style={{ borderRadius: 12 }}>
                          <Paragraph type="secondary">
                            Statut actuel: <Tag color={statusColor[user.status]}>{getStatusLabel(user.status)}</Tag>
                          </Paragraph>
                          <Button icon={<SafetyCertificateOutlined />} onClick={() => setStatusDialogOpen(true)}>
                            Modifier le statut
                          </Button>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                )
              }
            ] : [])
          ]}
        />
      </Card>

      {/* Modale Suppression */}
      <Modal
        title="Confirmer la suppression"
        open={deleteDialogOpen}
        onCancel={() => setDeleteDialogOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Annuler
          </Button>,
          <Button key="delete" type="primary" danger onClick={handleDeleteConfirm} loading={deleting}>
            Supprimer définitivement
          </Button>
        ]}
      >
        <Text>Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.</Text>
      </Modal>

      {/* Modale Changement de Statut */}
      <Modal
        title="Changer le statut de l'utilisateur"
        open={statusDialogOpen}
        onCancel={() => setStatusDialogOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setStatusDialogOpen(false)} disabled={updatingStatus}>
            Annuler
          </Button>,
          <Button key="save" type="primary" onClick={handleUpdateStatus} loading={updatingStatus}>
            Enregistrer
          </Button>
        ]}
      >
        <Form layout="vertical" style={{ paddingTop: 16 }}>
          <Form.Item label="Nouveau statut">
            <Select
              value={newStatus}
              onChange={(val) => setNewStatus(val as UserStatus)}
              style={{ width: '100%' }}
              options={[
                { value: 'active', label: 'Actif' },
                { value: 'inactive', label: 'Inactif' },
                { value: 'suspended', label: 'Suspendu' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modale Changement de Mot de passe */}
      <Modal
        title="Changer le mot de passe"
        open={passwordDialogOpen}
        onCancel={() => !changingPassword && setPasswordDialogOpen(false)}
        width={450}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setPasswordDialogOpen(false);
              setNewPassword('');
              setConfirmPassword('');
              setError(null);
            }}
            disabled={changingPassword}
          >
            Annuler
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleChangePassword}
            loading={changingPassword}
            disabled={!newPassword || !confirmPassword}
          >
            Mettre à jour
          </Button>
        ]}
      >
        <div style={{ paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && <Alert message={error} type="error" closable onClose={() => setError(null)} />}
          <Input.Password
            placeholder="Nouveau mot de passe (min 6 caractères)"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
          />
          <Input.Password
            placeholder="Confirmer le mot de passe"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
          />
        </div>
      </Modal>
    </div>
  );
};

export default UserDetail;
